import axios from 'axios';
import type { CreateUser, UpdateUser, Budget, Transaction, ParsedExpense, RecurringExpense, OptionDoc, MetadataOptions, MonthlyBudgetResponse } from 'src/types/types';
import { auth, signInWithEmailAndPassword, signInWithPopup, googleProvider } from 'src/api/firebase';
import type { User as FirebaseUser } from 'firebase/auth';

export const API = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8080',
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 1000000,
});

API.interceptors.request.use(async (config) => {
  const token = await auth.currentUser?.getIdToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Auth ─────────────────────────────────────────────────────────────────────
const loginWithEmail = async (email: string, password: string): Promise<FirebaseUser> => {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
};

const loginWithGoogle = async (): Promise<FirebaseUser> => {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
};

// ── Users ────────────────────────────────────────────────────────────────────
const getUser = async (id: string) => {
  const response = await API.get(`/users/${id}`);
  return response.data;
};

const createUser = async (user: CreateUser) => {
  const response = await API.post('/users', user);
  return response.data;
};

const updateUser = async (userId: string, user: UpdateUser) => {
  const response = await API.put(`/users/${userId}`, user);
  return response.data;
};

// ── Budgets ───────────────────────────────────────────────────────────────────
const getBudgets = async (): Promise<Budget[]> => {
  const response = await API.get('/budgets');
  return response.data.data;
};

// ── Transactions ──────────────────────────────────────────────────────────────
const getTransactions = async (startDate?: string, endDate?: string): Promise<Transaction[]> => {
  const params: Record<string, string> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  const response = await API.get('/transactions', { params });
  return response.data.data;
};

const createTransaction = async (data: Omit<Transaction, 'id'>): Promise<Transaction> => {
  const response = await API.post('/transactions', data);
  return response.data.data;
};

const updateTransaction = async (id: string, data: Partial<Transaction>): Promise<Transaction> => {
  const response = await API.put(`/transactions/${id}`, data);
  return response.data.data;
};

const deleteTransaction = async (id: string): Promise<void> => {
  await API.delete(`/transactions/${id}`);
};

// ── Recurring Expenses ────────────────────────────────────────────────────────
const getRecurring = async (): Promise<RecurringExpense[]> => {
  const response = await API.get('/recurring');
  return response.data.data;
};

const createRecurring = async (data: Omit<RecurringExpense, 'id'>): Promise<RecurringExpense> => {
  const response = await API.post('/recurring', data);
  return response.data.data;
};

const updateRecurring = async (id: string, data: Partial<RecurringExpense>): Promise<RecurringExpense> => {
  const response = await API.put(`/recurring/${id}`, data);
  return response.data.data;
};

const deleteRecurring = async (id: string): Promise<void> => {
  await API.delete(`/recurring/${id}`);
};

// ── Monthly Budget ────────────────────────────────────────────────────────────
const getMonthlyBudget = async (year: number, month: number): Promise<MonthlyBudgetResponse> => {
  const response = await API.get('/monthly-budget', { params: { year, month } });
  return response.data.data;
};

// ── Metadata ──────────────────────────────────────────────────────────────────
const getMetadata = async (): Promise<MetadataOptions> => {
  const response = await API.get('/metadata');
  return response.data.data;
};

const upsertMetadataItem = async (sub: string, doc: OptionDoc): Promise<OptionDoc> => {
  const response = await API.post(`/metadata/${sub}`, doc);
  return response.data.data;
};

const updateMetadataItem = async (sub: string, id: string, data: Partial<OptionDoc>): Promise<OptionDoc> => {
  const response = await API.put(`/metadata/${sub}/${id}`, data);
  return response.data.data;
};

const deleteMetadataItem = async (sub: string, id: string): Promise<void> => {
  await API.delete(`/metadata/${sub}/${id}`);
};

// ── AI ────────────────────────────────────────────────────────────────────────
const parseAIExpense = async (text: string): Promise<ParsedExpense> => {
  const response = await API.post('/ai/parse', { text });
  return response.data.data;
};

export default {
  loginWithEmail,
  loginWithGoogle,
  getUser,
  createUser,
  updateUser,
  getBudgets,
  getTransactions,
  createTransaction,
  updateTransaction,
  deleteTransaction,
  parseAIExpense,
  getRecurring,
  createRecurring,
  updateRecurring,
  deleteRecurring,
  getMonthlyBudget,
  getMetadata,
  upsertMetadataItem,
  updateMetadataItem,
  deleteMetadataItem,
};
